import { useEffect, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { SEO } from "@/components/seo/SEO";
import { useAuth } from "@/hooks/useAuth";
import { useEntitlement } from "@/hooks/useEntitlement";
import { useSpacedRepetition } from "@/hooks/useSpacedRepetition";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { EnhancedStatCard } from "@/components/dashboard/EnhancedStatCard";
import { RecentActivityTable } from "@/components/dashboard/RecentActivityTable";
import { AdminOverview } from "@/components/dashboard/AdminOverview";
import { ResumeQuizCard } from "@/components/dashboard/ResumeQuizCard";
import { SpacedReviewWidget } from "@/components/dashboard/SpacedReviewWidget";
import { PerformanceGoalsWidget } from "@/components/dashboard/PerformanceGoalsWidget";
import {
  CheckCircle2, Target, TrendingUp, Plus, Shield, Flame,
  BarChart3, ArrowRight, Calendar
} from "lucide-react";

const sb = supabase as any;

const Dashboard = () => {
  const { user, loading } = useAuth();
  const { isAdmin, loading: entitlementLoading } = useEntitlement();
  const { dueCount } = useSpacedRepetition();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) {
      navigate('/auth');
    }
  }, [user, loading, navigate]);

  // Fetch user statistics
  const { data: stats } = useQuery({
    queryKey: ["dashboard-stats", user?.id],
    enabled: !!user && !isAdmin,
    queryFn: async () => {
      const { data: attempts } = await sb
        .from("attempts")
        .select("is_correct, attempted_at")
        .eq("user_id", user!.id);

      const total = attempts?.length || 0;
      const correct = attempts?.filter((a: any) => a.is_correct).length || 0;
      const accuracy = total > 0 ? Math.round((correct / total) * 100) : 0;

      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
      const thisWeekAttempts = attempts?.filter(
        (a: any) => new Date(a.attempted_at) >= oneWeekAgo
      ) || [];

      return {
        questionsAnswered: total,
        correctAnswers: correct,
        accuracy,
        thisWeekAttempts: thisWeekAttempts.length,
      };
    },
  });

  // Study streak calculation
  const { data: streak = 0 } = useQuery({
    queryKey: ["study-streak", user?.id],
    enabled: !!user && !isAdmin,
    queryFn: async () => {
      const { data: attempts } = await sb
        .from("attempts")
        .select("attempted_at")
        .eq("user_id", user!.id)
        .order("attempted_at", { ascending: false });

      if (!attempts || attempts.length === 0) return 0;

      const dates = [...new Set(attempts.map((a: any) => 
        new Date(a.attempted_at).toISOString().split('T')[0]
      ))].sort((a: string, b: string) => b.localeCompare(a));

      let streakCount = 0;
      const today = new Date().toISOString().split('T')[0];
      const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
      
      if (dates[0] !== today && dates[0] !== yesterday) return 0;
      
      for (let i = 0; i < dates.length; i++) {
        const expected = new Date(Date.now() - i * 86400000).toISOString().split('T')[0];
        if (dates[i] === expected) {
          streakCount++;
        } else if (i === 0 && dates[0] === yesterday) {
          // Allow starting from yesterday
          const exp2 = new Date(Date.now() - (i + 1) * 86400000).toISOString().split('T')[0];
          if (dates[i] === exp2) streakCount++;
          else break;
        } else {
          break;
        }
      }
      return streakCount;
    },
  });

  // Peer comparison
  const { data: peerAvg } = useQuery({
    queryKey: ["peer-avg-accuracy"],
    enabled: !!user && !isAdmin,
    queryFn: async () => {
      const { data } = await sb
        .from("attempts")
        .select("is_correct");
      if (!data || data.length === 0) return 0;
      const correct = data.filter((a: any) => a.is_correct).length;
      return Math.round((correct / data.length) * 100);
    },
  });

  // Question of the Day
  const { data: qotd } = useQuery({
    queryKey: ["question-of-day", new Date().toISOString().split('T')[0]],
    enabled: !!user && !isAdmin,
    queryFn: async () => {
      const today = new Date().toISOString().split('T')[0];
      // Use date as seed for consistent daily question
      const seed = today.replace(/-/g, '');
      const { data } = await sb
        .from("mcqs")
        .select("id, stem, option_a, option_b, option_c, option_d, correct_option, explanation, difficulty")
        .eq("lifecycle_status", "active")
        .limit(1)
        .order("created_at", { ascending: true })
        .range(parseInt(seed) % 1000, parseInt(seed) % 1000);
      return data?.[0] || null;
    },
  });

  if (loading || entitlementLoading) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto" />
          <p className="text-muted-foreground">
            {loading ? "Authenticating..." : "Loading permissions..."}
          </p>
        </div>
      </div>
    );
  }

  if (!user) return null;

  if (isAdmin) {
    return (
      <div className="space-y-8">
        <SEO title="QuickMedicalReview — Admin Dashboard" description="System overview, analytics, and admin tools." path="/dashboard" />
        <div className="flex items-center gap-2 p-3 bg-primary/10 border border-primary/20 rounded-lg">
          <Shield className="h-5 w-5 text-primary" />
          <span className="font-medium text-primary">Admin Dashboard Mode</span>
          <Badge variant="secondary" className="ml-auto">System View</Badge>
        </div>
        <AdminOverview />
      </div>
    );
  }

  const examReadiness = stats ? Math.min(100, Math.round(
    (stats.accuracy * 0.6) + (Math.min(stats.questionsAnswered, 500) / 500 * 40)
  )) : 0;

  return (
    <div className="space-y-6 animate-fade-in max-w-7xl mx-auto">
      <SEO title="QuickMedicalReview — Dashboard" description="Track your progress, goals, streaks, and performance analytics." path="/dashboard" />

      {/* Hero */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground mb-1">
            Welcome back, {user.user_metadata?.full_name || user.email?.split('@')[0] || 'User'}!
          </h1>
          <p className="text-muted-foreground">Your exam preparation at a glance</p>
        </div>
        <Button size="lg" asChild className="gap-2 bg-gradient-medical">
          <Link to="/study">
            <Plus className="h-5 w-5" />
            Continue Studying
          </Link>
        </Button>
      </div>

      {/* Resume in-progress quiz (only renders if applicable) */}
      <ResumeQuizCard />

      {/* 3 KPI cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card className="medical-card">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="feature-icon bg-orange-100 text-orange-600">
              <Flame className="h-6 w-6" />
            </div>
            <div className="flex-1">
              <div className="text-sm text-muted-foreground">Study Streak</div>
              <div className="text-3xl font-bold font-manrope">{streak} day{streak !== 1 ? 's' : ''}</div>
            </div>
          </CardContent>
        </Card>
        <EnhancedStatCard
          title="Accuracy"
          value={`${stats?.accuracy || 0}%`}
          type="gauge"
          gaugeValue={stats?.accuracy || 0}
          gaugeLabel={stats?.accuracy && stats.accuracy >= 80 ? "Excellent" : "Good"}
          icon={TrendingUp}
        />
        <Card className="medical-card">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="relative w-16 h-16">
              <svg className="w-16 h-16 -rotate-90" viewBox="0 0 36 36">
                <circle cx="18" cy="18" r="14" fill="none" stroke="hsl(var(--muted))" strokeWidth="3" />
                <circle cx="18" cy="18" r="14" fill="none" stroke="hsl(var(--primary))" strokeWidth="3"
                  strokeDasharray={`${examReadiness * 0.88} 88`} strokeLinecap="round" />
              </svg>
              <span className="absolute inset-0 flex items-center justify-center text-sm font-bold">{examReadiness}%</span>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Exam Readiness</div>
              <div className="text-lg font-bold font-manrope">
                {examReadiness >= 80 ? 'Excellent' : examReadiness >= 60 ? 'Good Progress' : 'Keep Going'}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabbed content */}
      <Tabs defaultValue="today" className="space-y-4">
        <TabsList>
          <TabsTrigger value="today">Today</TabsTrigger>
          <TabsTrigger value="progress">Progress</TabsTrigger>
          <TabsTrigger value="activity">Activity</TabsTrigger>
        </TabsList>

        <TabsContent value="today" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <PerformanceGoalsWidget />
            <SpacedReviewWidget />
          </div>
          {qotd && (
            <Card className="medical-card border-primary/20">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Calendar className="h-5 w-5 text-primary" />
                  Question of the Day
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-foreground mb-3 line-clamp-2">{qotd.stem}</p>
                <Button size="sm" className="bg-gradient-medical" asChild>
                  <Link to={`/practice`}>Answer Now <ArrowRight className="ml-2 h-4 w-4" /></Link>
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="progress" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-3">
            <EnhancedStatCard
              title="Questions Answered"
              value={stats?.questionsAnswered || 0}
              change={{ value: stats?.thisWeekAttempts || 0, label: "this week" }}
              icon={CheckCircle2}
            />
            <EnhancedStatCard
              title="Correct Answers"
              value={stats?.correctAnswers || 0}
              change={{ value: Math.round((stats?.thisWeekAttempts || 0) * 0.7), label: "this week" }}
              icon={Target}
            />
            <EnhancedStatCard
              title="Accuracy Rate"
              value={`${stats?.accuracy || 0}%`}
              type="gauge"
              gaugeValue={stats?.accuracy || 0}
              gaugeLabel={stats?.accuracy && stats.accuracy >= 80 ? "Excellent" : "Good"}
              icon={TrendingUp}
            />
          </div>
          {peerAvg !== undefined && stats && (
            <Card className="medical-card">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <BarChart3 className="h-5 w-5 text-primary" />
                  Peer Comparison
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <div className="text-sm text-muted-foreground mb-1">Your Accuracy</div>
                    <div className="text-2xl font-bold text-primary">{stats.accuracy}%</div>
                    <div className="w-full bg-muted rounded-full h-2 mt-2">
                      <div className="bg-primary h-2 rounded-full transition-all" style={{ width: `${stats.accuracy}%` }} />
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground mb-1">Peer Average</div>
                    <div className="text-2xl font-bold text-muted-foreground">{peerAvg}%</div>
                    <div className="w-full bg-muted rounded-full h-2 mt-2">
                      <div className="bg-muted-foreground/50 h-2 rounded-full transition-all" style={{ width: `${peerAvg}%` }} />
                    </div>
                  </div>
                </div>
                {stats.accuracy > peerAvg ? (
                  <p className="text-sm text-medical-green mt-3 flex items-center gap-1">
                    <TrendingUp className="h-4 w-4" /> You're performing above the peer average!
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground mt-3">
                    Keep practicing to improve your ranking against peers.
                  </p>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="activity">
          <RecentActivityTable userId={user.id} limit={10} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Dashboard;
